import {environment} from "../../../../../../env.ts";
import type ProfileStore from "../../../../../../stores/ProfileStore.tsx";
import type {JSONSerializableGridColDef} from "../../../../../../components/muix-data-grid/MuiXColumnDefinitions.ts";
import type {CustomReviewColumnTypes} from "./ColumnTypes";
import {GRID_ACTIONS_COL_DEF, GRID_ACTIONS_COLUMN_TYPE, GRID_CHECKBOX_SELECTION_FIELD,} from "@mui/x-data-grid-premium";
import {GRID_REVIEW_ACTION_FIELD} from "../cells/ReviewActionsCell.tsx";
import type {GROUP_KEY, GROUP_VAL} from "../CategorizationTableDataModel.ts";
import {CATEGORY_FILTER_OPERATORS} from "../filters/CategoryFilter.tsx";
import {
    BUSINESS_UNIT_FILTER_FIELD,
    CATEGORY_FILTER_FIELD
} from "../../../../controllers/CategorizationReviewPageFilterController.ts";
import type {BagStore} from "../../../../../../stores/BagStore.tsx";

type C = JSONSerializableGridColDef<CustomReviewColumnTypes>;

export function getCategorizationTableColumns(profileStore: ProfileStore, bagStore: BagStore, isMithraStaffGroup: boolean, databag: number, showSelection: boolean, isGroupBy: boolean): C[] {
    const columns: C[] = [];
    if (showSelection) columns.push({
        field: GRID_CHECKBOX_SELECTION_FIELD,
        serializableType: 'custom',
    });
    if (isGroupBy) columns.push({
        field: 'supplier_id' satisfies typeof GROUP_KEY,
        serializableType: 'string',
        headerName: 'Supplier Name',
        width: 200,
        valueGetter: (value, row) => row.id,
        valueFormatter: (value, row) => row.s_name || row.group || '',
        filterable: false,
    });
    if (!isGroupBy) columns.push({
        field: 's_name' satisfies typeof GROUP_VAL,
        serializableType: 'string',
        googleLink: {type: 'part_with_supplier'},
        ...bagStore.configStore.getPartialGridColDef('s_name', 'Supplier'),
    });
    if (isMithraStaffGroup || environment.isTestReviewPage ||
        bagStore.categorizationColumns.find(
            c => c.in_use && c.table_name === 'data_source_part' && c.column_name === 'p_id')
    ) columns.push({
        field: 'p_id',
        serializableType: 'string',
        isMithraStaffOnly: (isMithraStaffGroup || Boolean(environment.isTestReviewPage)),
        ...bagStore.configStore.getPartialGridColDef('p_id', 'Part ID'),
    });
    columns.push({
        field: 'p_spend',
        serializableType: 'number-currency',
        ...bagStore.configStore.getPartialGridColDef('p_spend', 'Spend'),
    });
    columns.push({
        // This one is always hidden, but still needs to be defined to ensure the filter-menu does not crash
        field: BUSINESS_UNIT_FILTER_FIELD,
        serializableType: 'string',
        // Important: filterOperators may never be []
        filterable: false,
        ...bagStore.configStore.getPartialGridColDef('business_unit_id', 'Selected Business Unit'),
    });
    columns.push({
        field: 'previous_cat',
        serializableType: 'custom-previous-cat',
        align: 'left',
        cellClassName: 'category-cell',
        ...bagStore.configStore.getPartialGridColDef('previous_cat', 'Previous category'),
    });
    if (environment.isTestReviewPage || bagStore.configStore.getConfigKey('CATEGORIZATION_SCORE')) columns.push({
        field: 'suggestion_score',
        serializableType: 'custom-score',
        ...bagStore.configStore.getPartialGridColDef('suggestion_score', 'Score'),
    });
    columns.push({
        field: CATEGORY_FILTER_FIELD,
        serializableType: 'custom-cat',
        align: 'left',
        cellClassName: 'category-cell',
        ...bagStore.configStore.getPartialGridColDef('cat', 'Category'),
        filterOperators: CATEGORY_FILTER_OPERATORS,
    }, {
        field: 'reasoning',
        serializableType: 'custom-reasoning',
        align: 'left',
        cellClassName: 'reasoning-cell',
        ...bagStore.configStore.getPartialGridColDef('reasoning', 'Reason'),
    }, {
        field: 'state',
        serializableType: 'custom-status',
        align: 'left',
        resizable: false,
        ...bagStore.configStore.getPartialGridColDef('state', 'Status'),
    });
    if (bagStore.configStore.getConfigKey('CATEGORIZATION_FILE_DL') ||
        profileStore.isCategorizationShowFileDownloadHack(databag)) columns.push({
        field: 'file_download',
        serializableType: 'custom-download-file',
        headerName: 'File',
    });
    const column_fields = columns.map(c => c.field);
    bagStore.categorizationColumns.filter(
        c => c.in_use && !column_fields.includes(c.column_name)
    ).forEach(c => columns.push({
        field: c.column_name,
        serializableType: 'string',
        ...bagStore.configStore.getPartialGridColDef(c.column_name, c.column_name),
    }));
    // Actions column
    columns.push({
        ...GRID_ACTIONS_COL_DEF,
        field: GRID_REVIEW_ACTION_FIELD,
        type: GRID_ACTIONS_COLUMN_TYPE,
        serializableType: GRID_ACTIONS_COLUMN_TYPE,
        minWidth: 70,
    });
    // Check if there are duplicates
    const seen = columns.map(c => c.field);
    if (seen.length !== new Set(seen).size) {
        console.error('Duplicate columns in categorization table', seen);
    }
    return columns;
}
